import { Badge, Button, Heading, Text } from "@/design-system";
import { useScrollDepth } from "@/hooks";
import { trackEvent } from "@/lib/dataLayer";
import { CookieConsent } from "@/pages/landingPage/components";
import { Helmet } from "react-helmet-async";
import { PartnerBenefits } from "./components/partnerBenefits";
import { PartnerFooter } from "./components/partnerFooter";
import { PartnerForm } from "./components/partnerForm";
import { PartnerHeader } from "./components/partnerHeader";
import { PartnerSteps } from "./components/partnerSteps";
import {
  DESCRIPTION,
  FORM_SECTION_ID,
  OG_TITLE,
  PATH,
  SITE_URL,
  TITLE,
} from "./constants";

/**
 * LP da parceria Fidexa + Alpha7. É um canal fechado para o time comercial da
 * Alpha7 indicar farmácias: por isso não tem menu, não linka para a home e
 * fica em `noindex` (fora do sitemap e do llms.txt), como a LP de tráfego pago.
 */
export const Alpha7 = () => {
  useScrollDepth();

  const trackCta = (location: "header" | "hero") =>
    trackEvent({ event: "cta_click_specialist", location });

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Início", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: TITLE, item: `${SITE_URL}${PATH}` },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={`${SITE_URL}${PATH}`} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Fidexa" />
        <meta property="og:title" content={OG_TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:url" content={`${SITE_URL}${PATH}`} />
        <meta property="og:image" content={`${SITE_URL}/og-image.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="pt_BR" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={OG_TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content={`${SITE_URL}/og-image.png`} />

        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      </Helmet>

      <div className="bg-ds-paper flex min-h-screen flex-col">
        <PartnerHeader onCtaClick={() => trackCta("header")} />
        <main className="flex flex-col gap-16 pb-16 md:gap-20 md:pb-20">
          <section
            aria-labelledby="hero-title"
            className="mx-auto flex w-full max-w-6xl flex-col items-start gap-6 px-4 pt-12 sm:px-6 md:pt-20 lg:px-8"
          >
            <Badge tone="brand" shape="pill">
              Parceria Alpha7
            </Badge>
            <Heading
              level={1}
              id="hero-title"
              className="md:text-ds-display max-w-3xl"
            >
              WhatsApp oficial da Meta para a farmácia vender, atender e
              cobrar sem risco de bloqueio
            </Heading>
            <Text size="lead" tone="muted" className="max-w-2xl">
              Campanhas de marketing, auto-atendimento, atendimento do time e
              cobrança no mesmo número, pela API oficial do WhatsApp: as
              mensagens chegam de forma estável, sem o risco de banimento das
              soluções não oficiais. Indique uma farmácia da Alpha7 e acompanhe
              o retorno.
            </Text>
            <Button asChild variant="primary" size="lg" onClick={() => trackCta("hero")}>
              <a href={`#${FORM_SECTION_ID}`}>Indicar farmácia</a>
            </Button>
          </section>

          <PartnerBenefits />
          <PartnerSteps />
          <PartnerForm />
        </main>
        <PartnerFooter />
        <CookieConsent />
      </div>
    </>
  );
};
